import React, { useEffect, useRef, useState } from 'react';
import api, { isLimitError } from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { parseAiJson } from '../utils/parseAiJson';
import { storage } from '../product/productStorage';
import { useCreateContext } from '../product/CreateContext';
import { CopyButton } from './create/generatorUi';
import { useCreateFlow, usePrefill } from './create/flow';
import './hook.css';

const PLATFORMS = [
    {
        id: 'tiktok',
        label: 'TikTok',
        linkInBio: true, // Links in captions aren't clickable
        limit: 2200,
        guidance: 'short punchy caption (1-2 sentences), 3-5 hashtags mixing broad and niche',
    },
    {
        id: 'instagram',
        label: 'Instagram',
        linkInBio: true,
        limit: 2200,
        guidance: 'engaging caption with a strong first line, line breaks and a call to action, 10-15 relevant hashtags',
    },
    {
        id: 'youtube',
        label: 'YouTube Shorts',
        limit: 5000,
        guidance: 'first line works as a searchable title, then a short keyword-rich description, 3-5 hashtags',
    },
    {
        id: 'facebook',
        label: 'Facebook',
        limit: 63206,
        guidance: 'conversational caption that invites comments, 1-3 hashtags',
    },
    {
        id: 'x',
        label: '𝕏',
        limit: 280,
        guidance: 'caption AND hashtags together must fit in 280 characters, 1-2 hashtags',
    },
    {
        id: 'linkedin',
        label: 'LinkedIn',
        limit: 3000,
        guidance: 'professional, insight-led post: a strong first line, short paragraphs, a question or call to action at the end, 3-5 hashtags',
    },
    {
        id: 'threads',
        label: 'Threads',
        limit: 500,
        guidance: 'conversational, 1-3 short sentences that invite replies; caption and hashtags together under 500 characters, 1-2 hashtags',
    },
];
const DEFAULT_PLATFORMS = ['tiktok', 'instagram', 'youtube'];

const TONES = [
    ['casual', 'Casual'],
    ['professional', 'Professional'],
    ['humorous', 'Humorous'],
    ['inspirational', 'Inspirational'],
    ['educational', 'Educational'],
];

const isText = (value) => typeof value === 'string' || typeof value === 'number';

const readLinkDestination = () => {
    const prefs = storage.getJson('planPreferences', {});
    return prefs && typeof prefs.linkDestination === 'string' ? prefs.linkDestination : '';
};

// Hashtags may come back as an array or one string; anything else is ignored
const normalizeHashtags = (hashtags) => {
    let list = [];
    if (Array.isArray(hashtags)) list = hashtags.filter(isText);
    else if (isText(hashtags)) list = String(hashtags).split(/[\s,]+/);
    const tags = list
        .map(tag => String(tag).trim().replace(/^#*/, ''))
        .filter(Boolean)
        .map(tag => `#${tag}`);
    // Tags are removed by name, so keep each one once
    return tags.filter((tag, index) => tags.findIndex(t => t.toLowerCase() === tag.toLowerCase()) === index);
};

// One platform's entry from the AI reply: {"caption", "hashtags"} or just the caption text
const normalizeEntry = (entry) => {
    if (isText(entry)) return { caption: String(entry).trim(), hashtags: [] };
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const caption = [entry.caption, entry.description, entry.text].find(isText);
    return {
        caption: caption === undefined ? '' : String(caption).trim(),
        hashtags: normalizeHashtags(entry.hashtags),
    };
};

// Platforms with clickable caption links get the tracked link above the hashtags;
// on TikTok / Instagram it goes in the bio instead
const formatPost = (result, platform) => {
    const link = result.link && !platform.linkInBio ? `\n\n${result.link}` : '';
    const tags = result.hashtags.join(' ');
    return `${result.caption}${link}${tags ? `\n\n${tags}` : ''}`;
};

// Tall enough to show most captions without scrolling
const captionRows = (caption) => Math.min(10, Math.max(3, caption.split('\n').reduce((rows, line) => rows + Math.ceil((line.length || 1) / 48), 0)));

const DescriptionScreen = () => {
    const { icp, promptContext, contentType, seed, embedded } = useCreateContext();
    const { flow, report } = useCreateFlow();
    const [topic, setTopic] = useState(() => flow.topic || seed?.topic || '');
    usePrefill(flow.topic, topic, setTopic);
    // The hook picked in the Hook step; it follows later picks until you type your own
    const [hook, setHook] = useState(() => flow.hook || seed?.hook || '');
    usePrefill(flow.hook, hook, setHook);
    const [tone, setTone] = useState('casual');
    const [linkDestination, setLinkDestination] = useState(readLinkDestination);
    // The planned post's platform is always one of them
    const [selectedPlatforms, setSelectedPlatforms] = useState(() => (
        PLATFORMS.some(p => p.id === flow.captionPlatform) && !DEFAULT_PLATFORMS.includes(flow.captionPlatform)
            ? [...DEFAULT_PLATFORMS, flow.captionPlatform]
            : DEFAULT_PLATFORMS
    ));
    // A caption the flow already has (from the plan's draft, or before a reload) shows as a result
    const [results, setResults] = useState(() => {
        if (!flow.caption && !flow.hashtags.length) return {};
        const platformId = PLATFORMS.some(p => p.id === flow.captionPlatform) ? flow.captionPlatform : DEFAULT_PLATFORMS[0];
        return { [platformId]: { caption: flow.caption, hashtags: flow.hashtags, ...(flow.link && { link: flow.link }) } };
    });
    // The platform whose caption goes into the post
    const [chosen, setChosen] = useState(() => Object.keys(results)[0] || '');
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState(null); // { field, message }
    const [error, setError] = useState('');
    const [linkWarning, setLinkWarning] = useState('');
    const topicRef = useRef(null);

    const togglePlatform = (platformId) => {
        setSelectedPlatforms(prev =>
            prev.includes(platformId)
                ? prev.filter(id => id !== platformId)
                : [...prev, platformId]
        );
        if (formError?.field === 'platforms') setFormError(null);
    };

    const handleGenerate = async () => {
        if (!topic.trim()) {
            setFormError({ field: 'topic', message: 'Describe what the post is about, then try again.' });
            topicRef.current?.focus();
            return;
        }
        if (selectedPlatforms.length === 0) {
            setFormError({ field: 'platforms', message: 'Pick at least one platform.' });
            return;
        }

        const platforms = PLATFORMS.filter(p => selectedPlatforms.includes(p.id));
        const prompt = `You are a social media manager and copywriter who knows what performs on each platform.
Write a post description with hashtags for each platform below.

Post / product: ${topic}
${hook ? `Video hook: ${hook}\n` : ''}Tone: ${tone}
${promptContext ? `\nWhat you know about the product, who it's for and the brand voice (write for this person, in this voice):\n${promptContext}\n` : ''}${linkDestination.trim() ? `End each caption with a short call to action to click the link (on ${platforms.filter(p => p.linkInBio).map(p => p.label).join(' and ') || 'no platform'}, say "link in bio"; elsewhere the link is added after the caption, so don't write a URL).\n` : ''}
Platform rules:
${platforms.map(p => `- ${p.id}: ${p.guidance}`).join('\n')}

Respond ONLY with JSON in this exact shape, no other text:
{${platforms.map(p => `"${p.id}": {"caption": "...", "hashtags": ["tag1", "tag2"]}`).join(', ')}}`;

        setFormError(null);
        setError('');
        setLinkWarning('');
        setIsLoading(true);
        try {
            const response = await api.getChatCompletion(prompt);
            const parsed = parseAiJson(response);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                throw new Error('Unexpected response');
            }

            const nextResults = {};
            platforms.forEach(p => {
                const entry = normalizeEntry(parsed[p.id]);
                if (!entry || (!entry.caption && entry.hashtags.length === 0)) return;
                nextResults[p.id] = entry;
            });
            if (Object.keys(nextResults).length === 0) {
                throw new Error('No platforms in response');
            }

            // One tracked link per platform so clicks are attributed to where they came from
            if (linkDestination.trim()) {
                try {
                    const links = await Promise.all(Object.keys(nextResults).map(platformId =>
                        api.createTrackedLink({
                            destination: linkDestination.trim(),
                            platform: PLATFORMS.find(p => p.id === platformId).label,
                            campaign: topic.trim().slice(0, 60),
                            hook,
                            icpId: icp?.id,
                            contentType: contentType || undefined,
                        })
                    ));
                    Object.keys(nextResults).forEach((platformId, index) => {
                        const shortUrl = links[index]?.shortUrl;
                        if (typeof shortUrl === 'string' && shortUrl) nextResults[platformId].link = shortUrl;
                    });
                } catch (linkError) {
                    const message = linkError.response?.data?.error;
                    setLinkWarning(typeof message === 'string' && message
                        ? message
                        : "Captions are ready, but the tracked links couldn't be created.");
                }
            }
            setResults(nextResults);
            // Keep the chosen platform when it's still there; else the planned post's platform, else the first
            setChosen(current => (nextResults[current] ? current
                : nextResults[flow.captionPlatform] ? flow.captionPlatform
                    : PLATFORMS.find(p => nextResults[p.id])?.id || ''));
        } catch (e) {
            if (isLimitError(e)) return;
            setError("Couldn't write captions this time. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const updateCaption = (platformId, caption) => {
        setResults(prev => ({ ...prev, [platformId]: { ...prev[platformId], caption } }));
    };

    const removeHashtag = (platformId, tag) => {
        setResults(prev => ({
            ...prev,
            [platformId]: {
                ...prev[platformId],
                hashtags: prev[platformId].hashtags.filter(t => t !== tag),
            },
        }));
    };

    // The chosen caption is the post's caption in the next steps
    useEffect(() => {
        const result = results[chosen];
        report('caption', {
            caption: result?.caption || '',
            hashtags: result?.hashtags || [],
            captionPlatform: result ? chosen : flow.captionPlatform,
            link: result?.link || '',
        }, Boolean(result && (result.caption.trim() || result.hashtags.length)));
    }, [results, chosen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Edits made in Review & post show here too
    useEffect(() => {
        setResults(previous => {
            const result = previous[chosen];
            if (!result || (result.caption === flow.caption && result.hashtags.join(' ') === flow.hashtags.join(' '))) return previous;
            return { ...previous, [chosen]: { ...result, caption: flow.caption, hashtags: flow.hashtags } };
        });
    }, [flow.caption, flow.hashtags]); // eslint-disable-line react-hooks/exhaustive-deps

    const resultPlatforms = PLATFORMS.filter(p => results[p.id]);

    return (
        <>
            {!embedded && (
                <div className="ls-page-header">
                    <h1 className="ls-page-title">Captions & hashtags</h1>
                </div>
            )}

            <div className="ls-two-col">
                <div className="ls-card">
                    <div>
                        <div className="ls-card-title">Caption brief</div>
                        <div className="ls-card-sub">One caption per platform, sized to its limits.</div>
                    </div>
                    <div className="ls-form ls-hook-form">
                        <div className="ls-section-title">Post</div>
                        <label className="ls-field">
                            <span className="ls-label">What is the post about?</span>
                            <textarea
                                ref={topicRef}
                                className="ls-textarea"
                                rows={3}
                                value={topic}
                                aria-invalid={formError?.field === 'topic'}
                                onChange={(e) => {
                                    setTopic(e.target.value);
                                    if (formError?.field === 'topic' && e.target.value.trim()) setFormError(null);
                                }}
                                placeholder="e.g. A 30 second demo of our budgeting app's new savings goals feature"
                            />
                        </label>
                        <label className="ls-field">
                            <span className="ls-label">Hook (optional)</span>
                            <input
                                className="ls-input"
                                type="text"
                                value={hook}
                                onChange={(e) => setHook(e.target.value)}
                                placeholder="The first line of the video"
                            />
                            <span className="ls-hint">The hook you pick in the Hook step shows up here.</span>
                        </label>

                        <div className="ls-section-title">Platforms</div>
                        <div className="ls-choices" role="group" aria-label="Platforms">
                            {PLATFORMS.map(p => (
                                <label key={p.id} className="ls-choice">
                                    <input
                                        type="checkbox"
                                        checked={selectedPlatforms.includes(p.id)}
                                        onChange={() => togglePlatform(p.id)}
                                    />
                                    {p.label}
                                </label>
                            ))}
                        </div>

                        <div className="ls-section-title">Style & link</div>
                        <div className="ls-form-grid">
                            <label className="ls-field">
                                <span className="ls-label">Tone</span>
                                <select className="ls-select" value={tone} onChange={(e) => setTone(e.target.value)}>
                                    {TONES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                                </select>
                            </label>
                            <label className="ls-field">
                                <span className="ls-label">Tracked link (optional)</span>
                                <input
                                    className="ls-input"
                                    type="url"
                                    value={linkDestination}
                                    onChange={(e) => setLinkDestination(e.target.value)}
                                    placeholder="https://yourstore.com/product"
                                />
                                <span className="ls-hint">Each platform gets its own short link, so clicks show up in Tracked links.</span>
                            </label>
                        </div>

                        {formError && <Alert severity="error" onClose={() => setFormError(null)}>{formError.message}</Alert>}

                        <div className="ls-hook-submit">
                            <button type="button" className={`ls-btn ${resultPlatforms.length > 0 ? 'ls-btn-secondary' : 'ls-btn-primary'}`} onClick={handleGenerate} disabled={isLoading}>
                                {isLoading ? <span className="ls-spinner" /> : <Icon name="auto_awesome" size={16} />}
                                {resultPlatforms.length > 0 ? 'Write new captions' : 'Write captions'}
                            </button>
                            <span className="ls-hint">
                                {selectedPlatforms.length === 1 ? 'For 1 platform' : `For ${selectedPlatforms.length} platforms`}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="ls-card" aria-busy={isLoading}>
                    <div>
                        <div className="ls-card-title">Captions</div>
                        <div className="ls-card-sub">
                            {resultPlatforms.length > 1
                                ? 'Edit freely and choose the one to post. The others are there to copy.'
                                : resultPlatforms.length === 1 ? 'Edit freely and remove hashtags you don\'t want.' : 'Your captions will show up here.'}
                        </div>
                    </div>

                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                    {linkWarning && <Alert severity="warning" onClose={() => setLinkWarning('')}>{linkWarning}</Alert>}

                    {resultPlatforms.length === 0 ? (
                        <div className="ls-placeholder">
                            {isLoading ? <span className="ls-spinner" /> : <Icon name="tag" />}
                            {isLoading ? 'Writing captions...' : 'Describe the post and pick platforms to get started.'}
                        </div>
                    ) : (
                        <div className="ls-results ls-stagger">
                            {resultPlatforms.map((p, index) => {
                                const result = results[p.id];
                                const post = formatPost(result, p);
                                const overLimit = post.length > p.limit;
                                const isChosen = chosen === p.id;
                                return (
                                    <div key={p.id} className={`ls-result${isChosen && resultPlatforms.length > 1 ? ' is-selected' : ''}`} style={{ '--i': index }}>
                                        <div className="ls-hook-row">
                                            <span className="ls-hook-platform">{p.label}</span>
                                            <span className={`ls-hook-count${overLimit ? ' is-over' : ''}`}>
                                                {post.length.toLocaleString()} / {p.limit.toLocaleString()}
                                            </span>
                                            <CopyButton text={post} />
                                        </div>
                                        {resultPlatforms.length > 1 && (
                                            <button
                                                type="button"
                                                className={`ls-btn ls-btn-pill ls-cflow-choose${isChosen ? ' is-on' : ''}`}
                                                aria-pressed={isChosen}
                                                onClick={() => setChosen(p.id)}
                                            >
                                                <Icon name={isChosen ? 'check_circle' : 'radio_button_unchecked'} size={16} filled={isChosen} />
                                                {isChosen ? 'Posting this one' : 'Post this one'}
                                            </button>
                                        )}
                                        <textarea
                                            className="ls-textarea is-compact"
                                            rows={captionRows(result.caption)}
                                            value={result.caption}
                                            onChange={(e) => updateCaption(p.id, e.target.value)}
                                            aria-label={`${p.label} caption`}
                                        />
                                        {result.hashtags.length > 0 && (
                                            <div className="ls-chips">
                                                {result.hashtags.map(tag => (
                                                    <span key={tag} className="ls-chip">
                                                        {tag}
                                                        <button
                                                            type="button"
                                                            className="ls-chip-remove"
                                                            aria-label={`Remove ${tag}`}
                                                            onClick={() => removeHashtag(p.id, tag)}
                                                        >
                                                            <Icon name="close" size={14} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {result.link && (
                                            <div className="ls-hint">
                                                {p.linkInBio ? 'Put this link in your bio: ' : 'Tracked link, included when you copy: '}
                                                <span className="ls-hook-link">{result.link}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DescriptionScreen;
